import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import {
  useAppSelector,
  useDeptProjectName,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
  useWorkLocationType,
} from '@Hooks/index';
import { FooterActions } from '@Enum/index';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import { parseDateTime } from '@Utils/transformDateTime';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import { NotificationContext } from '@Context/NotificationContext';
import {
  getAttendanceReasonsDict,
  getAttendanceTypesDict,
  getCompaniesDictSelector,
} from '@Store/selectors/dictionarySelector';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { ApproveListData } from '@Components/MainForm/components/ApproveList/types';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialValuesForm } from '../initialValuesForm';

interface AttendanceRequestApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

const AttendanceRequestApproveForm: React.FC<
  AttendanceRequestApproveFormProps
> = ({ formData, taskId }) => {
  const {
    taskApprovedNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const [postApproveRejectForm] = useRespondHrmRequestMutation();
  const attendanceTypes = useAppSelector(getAttendanceTypesDict);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Attendance');
  const { findDeptProjName } = useDeptProjectName();
  const { findWorkLocationType } = useWorkLocationType();
  const companies = useAppSelector(getCompaniesDictSelector);
  const reasonTypes = useAppSelector(getAttendanceReasonsDict);
  const [comments, setCommentValue] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      postApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        });
    },
    [comments, taskId, handleBackToListRequests]
  );

  useEffect(() => {
    if (!formData) {
      return;
    }

    formik.setValues({
      empNumber: formData.empNumber,
      currentLocation: formData.currentLocation,
      leaveTypeID: formData.leaveTypeID,
      startDate: formData.startDate,
      endDate: formData.endDate,
      groupID: Number(formData.groupID),
      remarks: formData.remarks,
      taReqToCompID: String(formData.taReqToCompID),
      wlId: formData.wlId,
      deptProjID: formData.deptProjID,
      totDays: formData.totDays,
    });
  }, [formData]);

  const dataSourceRequestDetails = useMemo(() => {
    const companyName =
      companies.find(
        (item) => item.compId === Number(formik.values.taReqToCompID)
      )?.compName || '';
    const reasonType =
      reasonTypes.find((item) => item.codeNum === Number(formik.values.groupID))
        ?.codeDesc || '';
    const requestTypeDesc =
      attendanceTypes.find((item) => item.codeNum === formik.values.leaveTypeID)
        ?.codeDesc || '';
    const workLocationDesc =
      findWorkLocationType(formik.values.wlId)?.codeDesc || '';
    const deptProjName = findDeptProjName(
      formik.values.deptProjID,
      formik.values.wlId
    );

    const output = [
      {
        label: 'Request Type',
        value: requestTypeDesc,
      },
      {
        label: 'Current Location',
        value: formik.values.currentLocation || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Company',
        value: companyName || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Location',
        value: workLocationDesc || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Department/Project',
        value: deptProjName || EMPTY_FIELD_PLACEHOLDER,
      },
      formik.values.startDate
        ? {
            label: 'Date',
            value: parseDateTime(formik.values.startDate),
          }
        : null,
      formik.values.endDate
        ? {
            label: 'To Date',
            value: parseDateTime(formik.values.endDate),
          }
        : null,
      formik.values.groupID
        ? {
            label: 'Reason',
            value: reasonType,
          }
        : null,
      formik.values.remarks
        ? {
            label: 'Remarks',
            value: formik.values.remarks,
          }
        : null,
    ];
    return output.filter((item) => Boolean(item)) as ApproveListData;
  }, [
    formik.values,
    findWorkLocationType,
    findDeptProjName,
    companies,
    reasonTypes,
    attendanceTypes,
  ]);

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        formType="approval"
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceRequestDetails}
            label="Request Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default AttendanceRequestApproveForm;
